import React from "react";
import { API_BASE_URL } from "../../utils/api";

const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "N/A";
const fmtDt = (d) => d ? new Date(d).toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "N/A";

const getFileName = (file) => file?.file_name || file?.filename || file?.name || file?.file_url || "Attachment";
const getFileUrl = (file) => {
  const path = file?.file_url || file?.url || file?.src || "";
  if (!path) return "";
  if (String(path).startsWith("http")) return path;
  return `${API_BASE_URL}${path}`;
};
const isImage = (file) => {
  if (file?.file_type) return String(file.file_type).startsWith("image/");
  return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(getFileName(file));
};

const ArchitecturalRequestPrintContent = ({
  requestDetails,
  member,
  comments = [],
  contractor,
  colors = [],
  files = [],
  filesLoading = false,
}) => {
  if (!requestDetails) return null;

  const r = requestDetails;
  const safeMember = member || {};
  const ownerName = `${safeMember.FirstName || safeMember.first_name || ""} ${safeMember.LastName || safeMember.last_name || ""}`.trim() || "N/A";
  const address = safeMember.FullAddress || safeMember.property_name || safeMember.Address || "N/A";
  const email = safeMember.EmailAddress || safeMember.email || "N/A";
  const phone = safeMember.PhoneNumber || safeMember.phone_number || "N/A";
  const altPhone = safeMember.AlternatePhoneNumber || "N/A";
  const memberCode = safeMember.ClubMemberCode || "N/A";

  const acknowledged = r.acknowledged === 0 || r.acknowledged === "0" || r.acknowledged === false;
  const imageFiles = files.filter(isImage);
  const otherFiles = files.filter((f) => !isImage(f));
  const hasConditions = r.conditions && String(r.conditions).trim().length > 0;
  const completionDays = r.completion_days_current || null;

  const statusColor = r.status === "Approved" ? "#22c55e"
    : r.status === "Rejected" || r.status === "Denied" ? "#ef4444"
    : r.status === "Pending" ? "#f59e0b"
    : "#6b7280";

  return (
    <div style={{ fontFamily: "'Segoe UI', Arial, sans-serif", color: "#1a202c", margin: 0, padding: 0, fontSize: "11pt" }}>

      {/* HEADER */}
      <div style={{ background: "linear-gradient(135deg, #1e3a5f 0%, #2f63ad 100%)", color: "#fff", padding: "24px 32px 20px", borderRadius: "0 0 8px 8px", marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: "9pt", letterSpacing: 2, textTransform: "uppercase", opacity: 0.75, marginBottom: 4 }}>Heritage Springs Community HOA</div>
            <div style={{ fontSize: "22pt", fontWeight: 700, marginBottom: 4 }}>Architectural Request</div>
            <div style={{ fontSize: "13pt", opacity: 0.9 }}>Request #{r.id}</div>
          </div>
          <div style={{ textAlign: "right", fontSize: "9pt", opacity: 0.85 }}>
            <div>Printed: {fmtDt(new Date())}</div>
            <div style={{ marginTop: 4 }}>My HOA Management System</div>
          </div>
        </div>
        <div style={{ marginTop: 16, display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ background: statusColor, color: "#fff", padding: "4px 14px", borderRadius: 20, fontWeight: 700, fontSize: "10pt" }}>
            {r.status || "N/A"}
          </span>
          <span style={{
            background: acknowledged ? "rgba(34,197,94,0.25)" : "rgba(245,158,11,0.25)",
            color: acknowledged ? "#bbf7d0" : "#fde68a",
            padding: "4px 14px", borderRadius: 20, fontSize: "10pt", fontWeight: 600
          }}>
            {acknowledged ? "✓ Terms Acknowledged" : "⚠ Not Acknowledged"}
          </span>
        </div>
      </div>

      <div style={{ padding: "0 32px 32px" }}>

        {/* REQUEST OVERVIEW */}
        <Section title="Request Overview">
          <Grid2Col>
            <Field label="Request ID" value={`#${r.id}`} />
            <Field label="Status" value={r.status} />
            <Field label="Type" value={r.type_name} />
            <Field label="Subtype" value={r.subtype_name} />
            <Field label="Priority" value={r.priority} />
            <Field label="Assigned To" value={r.assigned_user_name || "Unassigned"} />
            <Field label="Requested At" value={fmtDt(r.requested_at)} />
            <Field label="Anticipated Start" value={fmt(r.anticipated_start_date)} />
            <Field label="Anticipated Completion" value={fmt(r.anticipated_completion_date)} />
            <Field label="Property ID" value={r.property_id} />
          </Grid2Col>
        </Section>

        {/* DESCRIPTION */}
        {r.description && (
          <Section title="Description">
            <div style={{ background: "#f7f9fc", border: "1px solid #e2e8f0", borderRadius: 6, padding: "10px 14px", lineHeight: 1.6, whiteSpace: "pre-line" }}>
              {r.description}
            </div>
          </Section>
        )}

        {/* CONDITIONS */}
        {hasConditions && (
          <Section title="Conditions">
            <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "10px 14px", lineHeight: 1.6, whiteSpace: "pre-line" }}>
              {r.conditions}
            </div>
          </Section>
        )}

        {/* MEMBER INFO */}
        <Section title="Owner Information">
          <Grid2Col>
            <Field label="Owner Name" value={ownerName} />
            <Field label="Member Code" value={memberCode} />
            <Field label="Address" value={address} />
            <Field label="Email" value={email} />
            <Field label="Phone" value={phone} />
            <Field label="Alt. Phone" value={altPhone} />
          </Grid2Col>
        </Section>

        {/* CONTRACTOR */}
        <Section title="Contractor Information">
          {contractor ? (
            <Grid2Col>
              <Field label="Company" value={contractor.company_name} />
              <Field label="Contact" value={contractor.contractor_name} />
              <Field label="Phone" value={contractor.phone_number} />
              <Field label="Notes" value={contractor.notes} />
            </Grid2Col>
          ) : (
            <div style={{ color: "#6b7280", fontStyle: "italic" }}>No contractor information added.</div>
          )}
        </Section>

        {/* COLOR SELECTION */}
        {colors.length > 0 && (
          <Section title="Color Selection">
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {colors.map((c, i) => (
                <div key={c.id || i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: i % 2 === 0 ? "#f7f9fc" : "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px" }}>
                  <div>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{c.scheme_name || "Unnamed Scheme"}</div>
                    <div style={{ fontSize: "9.5pt", color: "#4a5568" }}>Subtype: {c.subtype_name || "N/A"} &nbsp;·&nbsp; Color: {c.option_color_name || c.scheme_name || "N/A"}</div>
                    <div style={{ fontSize: "9pt", color: "#6b7280", marginTop: 2 }}>Added: {c.created_at ? fmtDt(c.created_at) : "N/A"}</div>
                  </div>
                  {c.scheme_hex && (
                    <div style={{ width: 28, height: 28, borderRadius: 6, border: "1px solid #cbd5e0", background: c.scheme_hex, flexShrink: 0 }} />
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* FREEFORM RESPONSES */}
        {r.freeform_responses?.length > 0 && (
          <Section title="Freeform Responses">
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {r.freeform_responses.map((resp, i) => (
                <div key={resp.id || i} style={{ background: "#f7f9fc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>{resp.subtype_name || "N/A"}</div>
                  <div style={{ fontSize: "9.5pt", color: "#4a5568", marginBottom: 4 }}>Type: {resp.type_name || "N/A"}</div>
                  <div style={{ color: "#1a202c", whiteSpace: "pre-line", lineHeight: 1.5 }}>{resp.field_content || "No response provided."}</div>
                  <div style={{ fontSize: "9pt", color: "#6b7280", marginTop: 4 }}>Added: {resp.created_at ? fmtDt(resp.created_at) : "N/A"}</div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* COMPLETION DAYS */}
        {completionDays && (
          <Section title="Completion Days">
            <Grid2Col>
              <Field label="Days to Complete" value={completionDays.days_to_complete ?? "N/A"} />
              <Field label="Recorded At" value={completionDays.recorded_at ? fmtDt(completionDays.recorded_at) : "N/A"} />
            </Grid2Col>
            {completionDays.notes && String(completionDays.notes).trim() && (
              <div style={{ marginTop: 10, background: "#f7f9fc", border: "1px solid #e2e8f0", borderRadius: 6, padding: "10px 14px" }}>
                <strong>Notes:</strong> {completionDays.notes}
              </div>
            )}
          </Section>
        )}

        {/* IMAGES */}
        {imageFiles.length > 0 && (
          <Section title="Image Attachments">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {imageFiles.map((f, i) => {
                const src = getFileUrl(f);
                const name = getFileName(f);
                return (
                  <div key={f.id || f.file_id || i} style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", maxWidth: 260 }}>
                    <img src={src} alt={name}
                      style={{ width: "100%", display: "block", maxHeight: 190, objectFit: "cover" }}
                      onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    <div style={{ padding: "6px 10px", fontSize: "9pt", color: "#4a5568" }}>{name}</div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {/* OTHER FILES */}
        {otherFiles.length > 0 && (
          <Section title="Other Attachments">
            <table style={tableStyle}>
              <thead><tr style={thRowStyle}><Th>File Name</Th></tr></thead>
              <tbody>
                {otherFiles.map((f, i) => (
                  <tr key={f.id || f.file_id || i} style={i % 2 === 0 ? {} : altRowStyle}>
                    <Td>{getFileName(f)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        {/* COMMENTS */}
        <Section title={`Comments (${comments.length})`}>
          {comments.length === 0
            ? <div style={{ color: "#6b7280", fontStyle: "italic" }}>No comments recorded.</div>
            : comments.map((c, i) => (
              <div key={c.comment_id || i} style={{ borderLeft: "3px solid #2f63ad", marginBottom: 12, background: i % 2 === 0 ? "#f7f9fc" : "#fff", borderRadius: "0 6px 6px 0", padding: "8px 12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontWeight: 700, color: "#2f63ad" }}>{c.user_name} {c.user_lastname}</span>
                  <span style={{ fontSize: "9pt", color: "#6b7280" }}>{fmtDt(c.sent_at)}</span>
                </div>
                <div style={{ color: "#1a202c", lineHeight: 1.5 }}>{c.comment_text}</div>
              </div>
            ))
          }
        </Section>

        {/* FOOTER */}
        <div style={{ marginTop: 32, borderTop: "2px solid #e2e8f0", paddingTop: 12, display: "flex", justifyContent: "space-between", fontSize: "9pt", color: "#6b7280" }}>
          <span>Heritage Springs Community HOA | My HOA Management System</span>
          <span>Printed: {fmtDt(new Date())}</span>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
      <div style={{ background: "#2f63ad", width: 4, height: 18, borderRadius: 2 }} />
      <div style={{ fontWeight: 700, fontSize: "11pt", color: "#1e3a5f", textTransform: "uppercase", letterSpacing: 0.5 }}>{title}</div>
    </div>
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px 16px" }}>{children}</div>
  </div>
);

const Grid2Col = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>{children}</div>
);

const Field = ({ label, value }) => (
  <div style={{ paddingBottom: 4 }}>
    <div style={{ fontSize: "8.5pt", color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 1 }}>{label}</div>
    <div style={{ fontWeight: 600, color: "#1a202c" }}>{value || "N/A"}</div>
  </div>
);

const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: "10pt" };
const thRowStyle = { background: "#f0f4fa" };
const altRowStyle = { background: "#f7f9fc" };
const Th = ({ children }) => (
  <th style={{ padding: "7px 10px", textAlign: "left", fontWeight: 700, color: "#1e3a5f", fontSize: "9pt", borderBottom: "2px solid #2f63ad" }}>{children}</th>
);
const Td = ({ children }) => (
  <td style={{ padding: "7px 10px", borderBottom: "1px solid #e2e8f0", color: "#1a202c" }}>{children}</td>
);

export default ArchitecturalRequestPrintContent;
